import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { qk } from '../../lib/query'
import { Icon } from '../icons'

/**
 * Reminders on a conversation.
 *
 * Exists because a thread can hold a commitment that no queue tracks: "vou solicitar o link dia
 * 01/09" leaves the conversation neither unread nor overdue, so it correctly disappears and is
 * then genuinely forgotten.
 *
 * The AI proposal is always shown WITH the sentence it came from. A date is easy to extract and
 * easy to get subtly wrong (which year? 01/09 or 09/01?), and an agent confirming against the
 * customer's own words catches that in a glance — confirming a bare date does not.
 */
export function ReminderBar({ conversationId }: { conversationId: string }) {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [due, setDue] = useState('')
  const [note, setNote] = useState('')

  const { data } = useQuery({
    queryKey: qk.reminders(conversationId),
    queryFn: () => api.reminders(conversationId),
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: qk.reminders(conversationId) })
    qc.invalidateQueries({ queryKey: ['conversations'] })
  }

  const create = useMutation({
    mutationFn: (body: { dueAt: string; note: string; source?: string }) => api.createReminder(conversationId, body),
    onSuccess: () => { setAdding(false); setDue(''); setNote(''); invalidate() },
  })
  const cancel = useMutation({
    mutationFn: (id: string) => api.cancelReminder(conversationId, id),
    onSuccess: invalidate,
  })

  const fmt = (iso: string) => new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
  const pending = data?.pending ?? []
  const proposal = data?.proposal ?? null

  if (pending.length > 0) {
    return (
      <div className="rem-bar">
        {pending.map((r) => (
          <span key={r.id} className="rem-chip">
            <Icon name="clock" size={12} />
            Lembrete {fmt(r.dueAt)} — {r.note}
            <button className="linkish" onClick={() => cancel.mutate(r.id)} title="Cancelar lembrete">
              cancelar
            </button>
          </span>
        ))}
      </div>
    )
  }

  if (proposal && !adding) {
    return (
      <div className="rem-bar proposal">
        <Icon name="clock" size={12} />
        <span>
          A cliente escreveu <b>“{proposal.matched}”</b> — criar lembrete para <b>{fmt(proposal.dueAt)}</b>?
          <br />
          <span className="rem-quote">“{proposal.quote}”</span>
        </span>
        <button
          className="btn pri sm"
          disabled={create.isPending}
          onClick={() =>
            create.mutate({
              dueAt: proposal.dueAt,
              note: `Retomar: “${proposal.quote.slice(0, 80)}”`,
              source: 'ai_suggested',
            })
          }
        >
          Criar
        </button>
        <button className="btn ghost sm" onClick={() => setAdding(true)}>Outra data</button>
      </div>
    )
  }

  if (!adding) {
    return (
      <div className="rem-bar">
        <button className="linkish" onClick={() => setAdding(true)}>
          <Icon name="clock" size={12} /> Criar lembrete
        </button>
      </div>
    )
  }

  return (
    <div className="rem-bar">
      <input
        type="date"
        value={due}
        onChange={(e) => setDue(e.target.value)}
        // Today would fire on the next sweep, which is never what someone means by "remind me".
        min={new Date(Date.now() + 86400_000).toISOString().slice(0, 10)}
      />
      <input
        placeholder="Motivo — quem ler daqui a duas semanas precisa entender"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        style={{ flex: 1, minWidth: 160 }}
      />
      <button
        className="btn pri sm"
        disabled={!due || !note.trim() || create.isPending}
        // Midday: a date input gives midnight UTC, which lands on the previous day in BRT.
        onClick={() => create.mutate({ dueAt: new Date(`${due}T12:00:00`).toISOString(), note })}
      >
        Salvar
      </button>
      <button className="btn ghost sm" onClick={() => setAdding(false)}>Cancelar</button>
      {create.isError && <span className="sug-err">Não foi possível salvar.</span>}
    </div>
  )
}
